import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MapPin, Send } from "lucide-react";
import api from "@/lib/api";
import { FadeIn, Reveal } from "@/components/Reveal";
import { inputCls, Label, GoldButton } from "@/components/Field";
import { usePageTitle } from "@/hooks/use-page-title";

const InquiryForm = ({ listing }) => {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads/contact", {
        ...form,
        inquiry_type: "Buying Land",
        message: `Inquiry about: ${listing.title} (ID: ${listing.id})\n\n${form.message}`,
      });
      toast.success("Inquiry sent", { description: "We'll be in touch about this property shortly." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="listing-inquiry-form"
      className="rounded-3xl border border-aspira-goldborder bg-white p-7 shadow-xl sm:p-8"
    >
      <h3 className="font-serif text-xl font-medium text-aspira-ink">Ask about this property</h3>
      <p className="mt-1.5 text-xs text-aspira-muted">Questions or ready to take the next step? Send us a note.</p>
      <div className="mt-6 grid gap-4">
        <div>
          <Label htmlFor="inq-name">Full name *</Label>
          <input id="inq-name" data-testid="inq-name" required className={inputCls} value={form.name} onChange={set("name")} />
        </div>
        <div>
          <Label htmlFor="inq-email">Email *</Label>
          <input id="inq-email" data-testid="inq-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} />
        </div>
        <div>
          <Label htmlFor="inq-phone">Phone</Label>
          <input id="inq-phone" data-testid="inq-phone" className={inputCls} value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <Label htmlFor="inq-message">Message *</Label>
          <textarea id="inq-message" data-testid="inq-message" required rows={4} className={inputCls} placeholder="I'd like to know more about…" value={form.message} onChange={set("message")} />
        </div>
      </div>
      <GoldButton type="submit" data-testid="inq-submit" disabled={sending} className="mt-6 w-full">
        {sending ? "Sending…" : "Send Inquiry"} <Send size={15} />
      </GoldButton>
    </form>
  );
};

const DetailRow = ({ label, value, testid }) =>
  value ? (
    <div className="flex items-start justify-between gap-4 border-b border-aspira-goldborder/50 py-3.5 last:border-0">
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-aspira-muted">{label}</span>
      <span className="text-right text-sm font-medium text-aspira-ink" data-testid={testid}>{value}</span>
    </div>
  ) : null;

const ListingDetail = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api
      .get(`/listings/${id}`)
      .then((r) => setListing(r.data))
      .catch(() => setMissing(true));
  }, [id]);

  usePageTitle(
    listing ? `${listing.title} | Aspira Property Group` : "Property | Aspira Property Group"
  );

  if (missing) {
    return (
      <section className="mx-auto max-w-3xl px-5 py-32 text-center sm:px-8" data-testid="listing-missing">
        <h1 className="font-serif text-3xl font-medium text-aspira-ink">This property is no longer listed.</h1>
        <p className="mt-4 text-sm text-aspira-body">It may have sold or been removed. Browse what else is available.</p>
        <Link to="/buy" data-testid="listing-missing-back" className="mt-8 inline-flex items-center gap-2 rounded-full bg-aspira-gold px-7 py-3.5 text-sm font-semibold text-white hover:bg-aspira-goldhover">
          View available land <ArrowRight size={15} />
        </Link>
      </section>
    );
  }

  if (!listing) {
    return <p className="mx-auto max-w-7xl px-5 py-32 text-sm text-aspira-muted sm:px-8" data-testid="listing-loading">Loading property…</p>;
  }

  return (
    <section data-testid="listing-detail" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:py-16">
      <FadeIn>
        <Link to="/buy" data-testid="listing-back" className="inline-flex items-center gap-2 text-sm font-semibold text-aspira-body transition-colors hover:text-aspira-gold">
          <ArrowLeft size={15} /> Back to all land
        </Link>
      </FadeIn>
      <div className="mt-8 grid items-start gap-10 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <FadeIn delay={0.1}>
            {listing.image_url ? (
              <img
                src={listing.image_url}
                alt={`${listing.title} — vacant land in ${[listing.county, listing.state].filter(Boolean).join(", ")}`}
                className="h-[340px] w-full rounded-3xl object-cover shadow-2xl lg:h-[460px]"
                data-testid="listing-photo"
              />
            ) : (
              <div className="flex h-[340px] items-center justify-center rounded-3xl bg-aspira-sand lg:h-[460px]">
                <MapPin size={32} className="text-aspira-gold" />
              </div>
            )}
          </FadeIn>
          <Reveal delay={0.15} className="mt-10">
            <h2 className="font-serif text-2xl font-medium text-aspira-ink">About this property</h2>
            <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-aspira-body sm:text-base" data-testid="listing-description">
              {listing.description || "Details about this property are available on request — send us an inquiry and we'll share everything we know."}
            </p>
          </Reveal>
        </div>
        <div className="space-y-8 lg:sticky lg:top-28">
          <FadeIn delay={0.2}>
            <div className="rounded-3xl border border-aspira-goldborder bg-white p-7 shadow-xl sm:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">Available land</p>
              <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight text-aspira-ink" data-testid="listing-title">
                {listing.title}
              </h1>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-aspira-body">
                <MapPin size={14} className="text-aspira-gold" />
                {[listing.county, listing.state].filter(Boolean).join(", ")}
              </p>
              {listing.price && (
                <p className="mt-4 font-serif text-3xl text-aspira-gold" data-testid="listing-price">{listing.price}</p>
              )}
              <div className="mt-6">
                <DetailRow label="Acreage" value={listing.acreage ? `${listing.acreage} acres` : ""} testid="listing-acreage" />
                <DetailRow label="State" value={listing.state} testid="listing-state" />
                <DetailRow label="County" value={listing.county} testid="listing-county" />
                <DetailRow label="Access" value={listing.access} testid="listing-access" />
                <DetailRow label="Features" value={listing.features} testid="listing-features" />
                <DetailRow label="Parcel #" value={listing.parcel_number} testid="listing-parcel" />
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={0.3}>
            <InquiryForm listing={listing} />
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default ListingDetail;
